import { createAttractionThemeTranslationsConfig, type AttractionThemeTranslation } from '@/config/objects/attraction-theme-translations';
import {
  GenerateThemeTranslationVoiceoversDialog,
  RegenerateThemeTranslationAction,
  RegenerateThemeTranslationIcon,
} from '@/components/attractions/themes/translations';
import { CrudDetailView, PageContainer, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AudioLines } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

/**
 * Attraction theme translation detail page
 */
export function AttractionThemeTranslationDetailPage() {
  const { attractionId, themeId, translationId } = useParams<{ attractionId: string; themeId: string; translationId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [translation, setTranslation] = useState<AttractionThemeTranslation | null>(null);
  const [voiceoversDialogOpen, setVoiceoversDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);

  // Map the loaded translation's sync flags to human-readable field labels and
  // decide whether offering a voiceover regen makes sense (description/long
  // description are the voiceover-backed fields).
  const outdatedFields = useMemo(() => {
    if (!translation) return [] as string[];
    const fields: string[] = [];
    if (translation.isNameOutdated) fields.push('Name');
    if (translation.isDescriptionOutdated) fields.push('Short description');
    if (translation.isLongDescriptionOutdated) fields.push('Long description');
    return fields;
  }, [translation]);
  const canRegenerateVoiceovers = Boolean(
    translation?.isDescriptionOutdated || translation?.isLongDescriptionOutdated,
  );

  const translationsConfig = useMemo(
    () => (attractionId && themeId ? createAttractionThemeTranslationsConfig(attractionId, themeId) : null),
    [attractionId, themeId],
  );

  /**
   * Delete translation mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attractions/${attractionId}/themes/${themeId}/translations/${id}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['ThemeTranslation'] });
        navigate(`/attractions/${attractionId}/themes/${themeId}`);
      } else {
        throw new Error(response.error || 'Failed to delete translation');
      }
    },
  });

  if (!attractionId || !themeId || !translationId || !translationsConfig) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Translation Not Found</h2>
          <p className="text-muted-foreground">The requested translation could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form
   */
  const handleEdit = (translation: AttractionThemeTranslation) => {
    navigate(`/attractions/${attractionId}/themes/${themeId}/translations/${translation.id}/edit`);
  };

  /**
   * Handle translation deletion
   */
  const handleDelete = async (translation: AttractionThemeTranslation) => {
    return toast.promise(deleteMutation.mutateAsync(translation.id), {
      loading: 'Deleting translation...',
      success: 'Translation deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete translation',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attractions', href: '/attractions' },
    { label: translation?.theme?.attraction?.name || 'Attraction', href: `/attractions/${attractionId}` },
    { label: 'Themes', href: `/attractions/${attractionId}` },
    { label: translation?.theme?.name || translation?.theme?.themeType?.name || 'Theme', href: `/attractions/${attractionId}/themes/${themeId}` },
    { label: 'Translations', href: `/attractions/${attractionId}/themes/${themeId}` },
    { label: translation?.language?.name || 'Translation', current: true },
  ];

  return (
    <PageContainer>
      <CrudDetailView
        config={translationsConfig}
        itemId={translationId}
        breadcrumbs={breadcrumbs}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onData={(translationData: AttractionThemeTranslation) => setTranslation(translationData)}
        customActions={[
          {
            key: 'regenerate-translation',
            label: 'Sync with source',
            icon: RegenerateThemeTranslationIcon,
            variant: 'secondary',
            onClick: () => setRegenerateDialogOpen(true),
          },
          {
            key: 'generate-voiceovers',
            label: 'Generate Voiceovers',
            icon: AudioLines,
            variant: 'secondary',
            onClick: () => setVoiceoversDialogOpen(true),
          },
        ]}
      />

      {/* Generate Voiceovers Dialog */}
      {attractionId && themeId && translationId && (
        <GenerateThemeTranslationVoiceoversDialog
          attractionId={attractionId}
          themeId={themeId}
          translationId={translationId}
          open={voiceoversDialogOpen}
          onClose={() => setVoiceoversDialogOpen(false)}
        />
      )}

      {/* Regenerate ("sync with source") Dialog */}
      {attractionId && themeId && translationId && (
        <RegenerateThemeTranslationAction
          attractionId={attractionId}
          themeId={themeId}
          translationId={translationId}
          open={regenerateDialogOpen}
          onOpenChange={setRegenerateDialogOpen}
          outdatedFields={outdatedFields}
          canRegenerateVoiceovers={canRegenerateVoiceovers}
        />
      )}
    </PageContainer>
  );
}
